import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { X, Package, Plus, Loader2, Check, XCircle, Printer, Download } from 'lucide-react';
import { useLanguage } from '../../../i18n/LanguageContext';
import { getPrintErrorMessage } from '../../../utils/printErrors';
import productionService, { StationAllocation, SubSession } from '../../../services/productionService';
import inventoryService from '../../../services/inventoryService';
import stationManagementService, { Operator, StationWithDetails } from '../../../services/stationManagementService';
import operatorService from '../../../services/operatorService';
import { getDateCode } from '../../../utils/dateUtils';
import barcodeService, { computeSerialNumber } from '../../../services/barcodeService';
import { generateLabelPreviewHtml, LabelData } from '../../../services/labelPrintService';
import LabelPreviewModal from '../../../components/LabelPreviewModal';
import { wsService } from '../../../services/websocket';
import { useSessionWebSocket } from './SessionDetailsModal/useSessionWebSocket';
import { useSubSessionOperations } from './SessionDetailsModal/useSubSessionOperations';
import { useSessionSplitting, StationSplitEntry } from './SessionDetailsModal/useSessionSplitting';
import SessionAssignmentTab from './SessionDetailsModal/SessionAssignmentTab';
import SessionDetailsTab from './SessionDetailsModal/SessionDetailsTab';
import SessionHistoryTab from './SessionDetailsModal/SessionHistoryTab';
import SessionBoxesTab from './SessionDetailsModal/SessionBoxesTab';
import SessionModals from './SessionDetailsModal/SessionModals';
import OverrideHistoryTable from './OverrideHistoryTable';
import { getStatusBadgeClass } from '../../../utils/statusColors';
import StatusDropdown from '../../../components/StatusDropdown';
import CancelSessionModal from '../../../components/CancelSessionModal';
import AddMaterialModal from './AddMaterialModal';
import { useAuth } from '../../../hooks/useAuth';
import { useOperatorEfficiency } from '../../../hooks/useOperatorEfficiency';

// Material interface for multi-material sessions
interface SessionMaterial {
  sub_session_number?: string;
  item_number: string;
  transfer_number?: string;
  item_name?: string;
  item_english_name?: string;
  item_chinese_name?: string;
  allocated_weight?: number;
  remainder_weight?: number;
  cases?: number;
  yield_factor?: number; // Decimal from backend (guaranteed by resolve_yield_factor)
  yield_factor_source?: string;
  brand?: string;
  material_location_code?: string;
  date_code?: string;
}

interface ProductionSession {
  session_number: string;
  product_sku: string;
  product_name: string;
  product_chinese_name?: string;
  status: string;
  station_id?: number;
  station_name?: string;
  raw_material_item_number?: string;
  raw_material_name?: string;
  raw_material_chinese_name?: string;
  raw_material_yield_factor?: number;
  raw_material_lot?: string;
  initial_material_weight?: number;
  target_output_weight?: number;
  actual_output_weight?: number;
  waste_weight?: number;
  yield_percentage?: number;
  started_at?: string | null;
  completed_at?: string | null;
  scheduled_date?: string;
  date_code?: string;
  total_boxes?: number;
  expected_boxes?: number;
  packing?: string;
  unit_weight?: number;
  brand?: string;
  material_location_code?: string;
  source_location?: string;
  linked_transfer_number?: string;
  notes?: string;
  assigned_operator_id?: string | number;
  assigned_operator_name?: string;
  created_by_name?: string;
  created_at?: string;
  materials?: SessionMaterial[];
}

export type SessionTab = 'assignment' | 'details' | 'history' | 'boxes' | 'overrides';

// Per WCO-2140: managers default to the Details tab.
// Non-managers retain the existing default ('assignment'), unchanged from prior behavior.
export const getDefaultSessionTab = (role?: string): SessionTab =>
  role === 'M' || role === 'manager' ? 'details' : 'assignment';

interface SessionDetailsModalProps {
  session: ProductionSession;
  onClose: () => void;
  initialTab?: SessionTab;
}


const SessionDetailsModal: React.FC<SessionDetailsModalProps> = ({ session: initialSession, onClose, initialTab = 'assignment' }) => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();

  // Fetch full session details (includes materials array)
  const { data: sessionData } = useQuery<ProductionSession>(
    ['session-details', initialSession.session_number],
    () => productionService.getProductionSession(initialSession.session_number),
    {
      enabled: !!initialSession.session_number,
      staleTime: 5000, // Short stale time to ensure fresh data
      refetchOnMount: 'always', // Always refetch when modal opens
      refetchInterval: 10000, // Auto-refetch every 10 seconds for real-time status updates
    }
  );

  // Use fetched data or fall back to initial session while loading
  const session = sessionData || initialSession;

  // Tab state
  const [activeTab, setActiveTab] = useState<SessionTab>(initialTab);

  // Boxes tab - expand cancelled boxes and discarded labels
  const [showAllCancelled, setShowAllCancelled] = useState(false);
  const [expandedDiscardedLabels, setExpandedDiscardedLabels] = useState<Set<string>>(new Set());

  // Label reprint state
  const [reprintBoxId, setReprintBoxId] = useState<string | null>(null);
  const [reprintReason, setReprintReason] = useState('');
  const [isReprinting, setIsReprinting] = useState(false);
  // Cancel session flow (shared modal — WCO-2706)
  const [showCancelModal, setShowCancelModal] = useState(false);
  const { user } = useAuth();
  const canPdf = user && ['A', 'S', 'M', 'admin', 'supervisor', 'manager'].includes(user.role);

  // PDF download/print handlers
  const handleDownloadPdf = useCallback(async () => {
    try {
      const blob = await productionService.downloadSessionPdf(session.session_number);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `session-${session.session_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('sessionDetails.pdfError'));
    }
  }, [session.session_number, t]);

  const handlePrintPdf = useCallback(async () => {
    try {
      const blob = await productionService.downloadSessionPdf(session.session_number);
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.addEventListener('load', () => {
          printWindow.print();
        });
      }
    } catch {
      toast.error(t('sessionDetails.pdfError'));
    }
  }, [session.session_number, t]);

  // Resolved printer MAC address for this session's station
  const [printerMac, setPrinterMac] = useState<string | null>(null);

  // Label copies setting (from DB) + printer MAC resolution + simulate printer
  const [numberOfCopies, setNumberOfCopies] = useState(1);
  const [simulatePrinter, setSimulatePrinter] = useState(false);
  useEffect(() => {
    productionService.getConfigSettings()
      .then(settings => {
        const lc = settings.find((s: any) => s.setting_key === 'label_copies');
        if (lc) setNumberOfCopies(Number(lc.setting_value) || 2);
        const sp = settings.find((s: any) => s.setting_key === 'simulate_printer');
        setSimulatePrinter(sp?.setting_value === 'true');

        // Resolve printer MAC: look up station's printer config key, then resolve actual MAC
        if (session?.station_id) {
          stationManagementService.getStationPrinters()
            .then(stations => {
              const stationPrinter = stations.find(s => s.station_id === session.station_id);
              if (stationPrinter?.printer_mac_id) {
                const printerEntry = settings.find((s: any) => s.setting_key === stationPrinter.printer_mac_id);
                if (printerEntry?.setting_value) {
                  setPrinterMac(printerEntry.setting_value);
                }
              }
            })
            .catch((err) => {
              console.warn('Failed to load printer IP config for session details modal', err);
              toast.error(t('messages.loadFailed').replace('{item}', 'printer config'));
            });
        }
      })
      .catch((err) => {
        console.warn('Failed to load station printer config for session details modal', err);
        toast.error(t('messages.loadFailed').replace('{item}', 'station printer config'));
      });

    const handleConfigUpdate = (data: any) => {
      if (data?.key === 'label_copies') setNumberOfCopies(Number(data.value) || 2);
    };
    wsService.on('config-updated', handleConfigUpdate);
    return () => { wsService.off('config-updated', handleConfigUpdate); };
  }, [session?.station_id]);

  // Label preview state (shown when Simulate Printer is ON)
  const [showLabelPreview, setShowLabelPreview] = useState(false);
  const [labelPreviewHtml, setLabelPreviewHtml] = useState('');


  // Fetch stations
  const { data: stations = [] } = useQuery<StationWithDetails[]>(
    ['supervisor-stations'],
    () => stationManagementService.getStations(),
    { staleTime: 30000 }
  );

  // Fetch operators (filtered by session's scheduled date if available - only those scheduled as CUT)
  // If scheduled_date is missing, backend returns all available operators (backward compatible)
  const { data: operators = [] } = useQuery<Operator[]>(
    ['operators', session.scheduled_date],
    () => stationManagementService.getOperators(session.scheduled_date),
    { staleTime: 30000 }
  );

  // Fetch packers (filtered by session's scheduled date if available - only those scheduled as PACK)
  // If scheduled_date is missing, backend returns all available packers (backward compatible)
  const { data: packers = [] } = useQuery<Operator[]>(
    ['packers', session.scheduled_date],
    () => stationManagementService.getPackers(session.scheduled_date),
    { staleTime: 30000 }
  );

  // Fetch location codes for source location dropdown
  const { data: locationCodes = [] } = useQuery(
    ['ics-location-codes'],
    () => inventoryService.getLocationCodes(),
    { staleTime: 60000 }
  );

  // Mutation for setting source location
  const setSourceLocationMutation = useMutation(
    (sourceWarehouse: string) => productionService.setSourceLocation(session.session_number, sourceWarehouse),
    {
      onSuccess: (data) => {
        toast.success(`Source location set. Transfer ${data.transfer?.transfer_number} created.`);
        queryClient.invalidateQueries(['session-details', session.session_number]);
        queryClient.invalidateQueries(['productionSessions']);
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.error || 'Failed to set source location';
        toast.error(errorMessage);
      },
    }
  );

  // Fetch sub-sessions if session might be split
  const { data: subSessions = [], refetch: refetchSubSessions } = useQuery<SubSession[]>(
    ['sub-sessions', session.session_number],
    () => productionService.getSubSessions(session.session_number),
    {
      enabled: !!session.session_number,
      staleTime: 5000, // Short stale time to ensure fresh status
      refetchOnMount: 'always', // Always refetch when modal opens
      refetchInterval: 10000, // Auto-refetch every 10 seconds for real-time status updates
    }
  );

  // Fetch session history (only when history tab is active)
  const { data: historyData, isLoading: isLoadingHistory, refetch: refetchHistory } = useQuery(
    ['session-history', session.session_number],
    () => productionService.getSessionHistory(session.session_number),
    {
      enabled: !!session.session_number && (activeTab === 'history' || activeTab === 'boxes'),
      staleTime: 5000,  // Reduced for faster updates
      refetchOnMount: 'always'
    }
  );

  // Real-time updates via WebSocket (8 event listeners)
  useSessionWebSocket({ sessionNumber: session.session_number, subSessions, refetchHistory });

  // Check if session can be edited — only 'pending' (transfer not yet submitted)
  const canEdit = session.status === 'pending';
  // Only completed and cancelled sessions are read-only
  // Active/in_progress sessions can still have assignments adjusted by supervisors
  const isReadOnly = ['completed', 'cancelled'].includes(session.status);

  // Per-sub-session editability: planned/ready sub-sessions are editable regardless of parent status
  const canEditSubSession = (ss: SubSession) => !['completed', 'cancelled'].includes(ss.status);

  // Split session mutation (stays in parent - tightly coupled with splitting state)
  const splitSessionMutation = useMutation(
    (stationAllocations: StationAllocation[]) =>
      productionService.assignStations(session.session_number, stationAllocations),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['productionSessions']);
        queryClient.invalidateQueries(['sub-sessions', session.session_number]);
        queryClient.invalidateQueries(['supervisor-stations']);
        toast.success(t('sessionDetails.sessionSplit'));
        setIsSplitting(false);
        setStationSplitEntries([{ station_id: null, station_name: '', material_weight: '', operator_id: null, packer_id: null, weight_mode: 'fixed' }]);
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.error || error?.message || t('sessionDetails.failedToSplitSession');
        toast.error(errorMessage);
      },
    }
  );

  // Session & sub-session lifecycle operations (start/pause/resume/cancel/complete)
  const {
    effectiveStatus, hasSingleSubSession, hasMultipleSubSessions, effectiveStationId,
    isSessionControlLoading,
    canCancelSession,
    getRemainingWeight,
    handlePauseSession,
    handleCancelSubSession, confirmCancelSubSession, handleCompleteSubSession, handleStatusChange,
    showPauseModal, setShowPauseModal, pauseReason, setPauseReason,
    showCancelSubSessionConfirm, setShowCancelSubSessionConfirm, cancelSubSessionId, setCancelSubSessionId, isCancellingSubSession,
    showCompleteModal, setShowCompleteModal, completeSubSessionId, setCompleteSubSessionId,
    completeReason, setCompleteReason, isCompletingSession,
  } = useSubSessionOperations({
    sessionNumber: session.session_number,
    sessionStatus: session.status,
    subSessions,
    stationId: session.station_id,
    onClose,
  });

  // Splitting, assignments, station change, subdivide, add material
  const {
    isSplitting, setIsSplitting, isSavingAssignments, stationSplitEntries, setStationSplitEntries,
    showAddMaterialModal, setShowAddMaterialModal, pendingMaterials, setPendingMaterials,
    showSubdivideModal, setShowSubdivideModal, subdivideSubSessionId, setSubdivideSubSessionId,
    subdivideWeight, setSubdivideWeight, subdivideReason, setSubdivideReason,
    subdivideReasonOther, setSubdivideReasonOther, subdivideStationId, setSubdivideStationId, isSubdividing,
    showUnsavedChangesModal, setShowUnsavedChangesModal,
    showStationChangeModal, setShowStationChangeModal, stationChangeReason, setStationChangeReason,
    stationChangeOtherReason, setStationChangeOtherReason, pendingStationChange, setPendingStationChange,
    stationConflictInfo,
    canSplit, hasMultipleMaterials, totalAllocatedWeight, isSplitValid, entriesBelowMinWeight, hasChanges,
    handleStartSplitting, updateStationEntry,
    handleSplitSession, handleSaveSubSessionAssignments, handleUnifiedSave,
    handleClose, handleConfirmDiscard,
    handleConfirmStationChange, handleConfirmQueuePriority, handleCancelQueuePriority,
    openSubdivideModal, handleAddMaterial, handleConfirmSubdivide,
  } = useSessionSplitting({
    session,
    sessionData,
    subSessions,
    stations,
    canEdit,
    canEditSubSession,
    isReadOnly,
    onClose,
    splitSessionMutation,
    user,
  });

  // Get available stations - show ALL enabled stations for queuing sub-sessions
  // Multiple PLANNED sub-sessions can be queued to the same station
  // The backend handles promotion logic (only 1 READY per station at a time)
  const availableStations = useMemo(() => {
    return stations.filter(s => s.enabled);
  }, [stations]);

  // Get available operators
  const availableOperators = useMemo(() => {
    return operators.filter(o => o.disabled !== 'true' && o.disabled !== '1');
  }, [operators]);

  // Get available packers
  const availablePackers = useMemo(() => {
    return packers.filter(p => p.disabled !== 'true' && p.disabled !== '1');
  }, [packers]);

  // Operator efficiency scores for this product
  const { efficiencyMap, isLoading: efficiencyLoading, sortBy: efficiencySortBy, setSortBy: setEfficiencySortBy } = useOperatorEfficiency(session.product_sku);

  // Sort available operators by efficiency rank when data is available
  const sortedOperators = useMemo(() => {
    if (efficiencyMap.size === 0) return availableOperators;
    return [...availableOperators].sort((a, b) => {
      const effA = efficiencyMap.get(a.employee_number);
      const effB = efficiencyMap.get(b.employee_number);
      // Operators with efficiency data come first, sorted by the selected rank
      if (effA && !effB) return -1;
      if (!effA && effB) return 1;
      if (!effA && !effB) return 0;
      const rankKey = efficiencySortBy === 'yield' ? 'yield_rank'
        : efficiencySortBy === 'speed' ? 'speed_rank' : 'composite_rank';
      return (effA![rankKey] || 999) - (effB![rankKey] || 999);
    });
  }, [availableOperators, efficiencyMap, efficiencySortBy]);


  // Reprint reason options (same as SupervisorOverrideModal)
  const reprintReasons = [
    { value: 'damaged', label: t('supervisorOverride.reasons.damaged') },
    { value: 'misprint', label: t('supervisorOverride.reasons.misprint') },
    { value: 'illegible', label: t('supervisorOverride.reasons.illegible') },
    { value: 'wrongInfo', label: t('supervisorOverride.reasons.wrongInfo') },
    { value: 'other', label: t('supervisorOverride.reasons.other') },
  ];

  // Handle label reprint
  const handleReprint = async (box: { id: string; box_number: string; actual_weight: number; barcode_number: string | null; packed_by: string | null; packed_by_name: string | null }) => {
    if (!reprintReason) {
      toast.error(t('supervisorOverride.selectReason'));
      return;
    }

    setIsReprinting(true);
    try {
      // Generate barcode data for the label
      const dateCode = getDateCode();
      const serialNumber = computeSerialNumber();
      const barcodeData = await barcodeService.generateBarcode(
        session.product_sku,
        false,
        box.actual_weight,
        dateCode,
        serialNumber
      );

      // Log for debugging

      // Send to Zebra printer via backend print-service with "REPRINTED" banner
      let printSucceeded = false;

      // Simulate printer mode: show label preview instead of printing
      if (simulatePrinter) {
        // Generate label preview HTML for TEST MODE (with REPRINTED banner)
        const now = new Date();
        const labelData: LabelData = {
          product_name: session.product_name,
          product_chinese: session.product_chinese_name || '',
          product_sku: session.product_sku,
          product_ingredient: '',
          lot_number: session.raw_material_lot || '',
          box_number: Number(box.box_number) || 0,
          barcode_number: barcodeData?.data || '',
          actual_weight: Number(box.actual_weight).toFixed(2),
          target_weight: Number(session.unit_weight || box.actual_weight).toFixed(2),
          weight_unit: 'lbs',
          packed_date: now.toLocaleDateString(),
          packed_time: now.toLocaleTimeString(),
          packed_timestamp: now.toISOString(),
          operator_id: user?.id || '',
          operator_name: user?.fullName || user?.full_name || '',
          station_number: session.station_id || 0,
          station_name: session.station_name || '',
          raw_material_lot: session.raw_material_lot || '',
          raw_material_type: '',
          production_location: 'T&B Food',
          qr_data: '',
        };
        const previewHtml = await generateLabelPreviewHtml(labelData, barcodeData, true); // true = isReprint
        setLabelPreviewHtml(previewHtml);
        setShowLabelPreview(true);
        toast.success('TEST MODE: Print skipped - showing preview');
        printSucceeded = true;
      } else {
        const now = new Date();
        try {
          const printResult = await operatorService.printLabel({
            label_data: {
              product_name: session.product_name,
              product_chinese: session.product_chinese_name || '',
              product_sku: session.product_sku,
              product_ingredient: '',
              lot_number: session.raw_material_lot || '',
              box_number: box.box_number,
              barcode_number: barcodeData?.data || '',
              actual_weight: Number(box.actual_weight).toFixed(2),
              target_weight: Number(session.unit_weight || box.actual_weight).toFixed(2),
              weight_unit: 'lbs',
              packed_date: now.toLocaleDateString(),
              packed_time: now.toLocaleTimeString(),
              packed_timestamp: now.toISOString(),
              operator_id: user?.id || '',
              operator_name: user?.fullName || user?.full_name || '',
              station_number: session.station_id || 0,
              station_name: session.station_name || '',
              raw_material_lot: session.raw_material_lot || '',
              raw_material_type: '',
              production_location: 'T&B Food',
            },
            barcode_data: barcodeData?.data || '',
            barcode_display_text: barcodeData?.displayText || barcodeData?.data || '',
            copies: numberOfCopies,
            is_reprint: true,
            station_id: session.station_id || undefined,
            printer_mac: printerMac || undefined,
          });
          if (printResult.success) {
            printSucceeded = true;
          } else {
            console.warn('Reprint label print failed:', printResult.error);
            toast.error(getPrintErrorMessage(t, printResult.error_code, printResult.error, t('supervisorOverride.reprintPrintFailed')));
          }
        } catch (printErr: any) {
          console.warn('Reprint print service error:', printErr.message);
          const errData = printErr.response?.data;
          toast.error(getPrintErrorMessage(t, errData?.error_code, errData?.error, t('supervisorOverride.printerUnavailable')));
        }
      }

      // Only record reprint if print succeeded
      if (printSucceeded) {
        // Call reprint endpoint — records the reprint in database
        await operatorService.reprintLabel(box.id, {
          supervisor_id: user?.id || '',
          reason: reprintReason,
          label_data: {
            product_name: session.product_name,
            product_chinese: session.product_chinese_name || '',
            product_sku: session.product_sku,
            weight: box.actual_weight,
            box_number: box.box_number,
            barcode: barcodeData?.data || '',
            displayText: barcodeData?.displayText || '',
          },
        });

        // Record the override in session history
        await operatorService.recordSupervisorOverride({
          override_type: 'label_reprint',
          supervisor_id: user?.id || '',
          supervisor_name: user?.fullName || user?.full_name || '',
          session_number: String(session.session_number),
          sub_session_number: undefined,
          operator_employee_number: box.packed_by || undefined,
          operator_name: box.packed_by_name || undefined,
          reason: reprintReason,
          details: {
            box_id: box.id,
            box_number: box.box_number,
            barcode: barcodeData?.data || '',
            product_number: session.product_sku,
            product_name: session.product_name,
          },
        });

        toast.success(t('supervisorOverride.reprintSuccess'));
        setReprintReason('');
        setReprintBoxId(null);
        // Refresh boxes list
        queryClient.invalidateQueries(['session-history', session.session_number]);
      }
    } catch (err) {
      console.error('Reprint failed:', err);
      toast.error('Reprint failed');
    } finally {
      setIsReprinting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-lg w-[1100px] h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b dark:border-gray-700">
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-indigo-100 dark:bg-indigo-900 rounded-lg">
                <Package className="w-6 h-6 text-indigo-600 dark:text-indigo-300" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {t('sessionDetails.title')}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {session.session_number}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {hasSingleSubSession && subSessions[0] ? (
                <StatusDropdown
                  currentStatus={effectiveStatus}
                  subSessionNumber={subSessions[0].sub_session_number}
                  hasStation={!!subSessions[0].station_id}
                  hasOperator={!!subSessions[0].assigned_operator}
                  onStatusChange={handleStatusChange}
                  disabled={hasMultipleSubSessions}
                  size="md"
                />
              ) : (
                <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${getStatusBadgeClass(effectiveStatus)}`}>
                  {t(`status.${effectiveStatus}`) || effectiveStatus}
                </span>
              )}
              {canPdf && (
                <>
                  <button
                    onClick={handlePrintPdf}
                    title={t('sessionDetails.printPdf')}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <Printer className="w-4 h-4" />
                    <span className="hidden sm:inline">{t('sessionDetails.printPdf')}</span>
                  </button>
                  <button
                    onClick={handleDownloadPdf}
                    title={t('sessionDetails.downloadPdf')}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    <span className="hidden sm:inline">{t('sessionDetails.downloadPdf')}</span>
                  </button>
                </>
              )}
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-4 mt-4">
            <button
              onClick={() => setActiveTab('assignment')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'assignment'
                  ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300'
                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
              }`}
            >
              {t('sessionDetails.assignment')}
            </button>
            <button
              onClick={() => setActiveTab('details')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'details'
                  ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300'
                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
              }`}
            >
              {t('sessionDetails.details')}
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'history'
                  ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300'
                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
              }`}
            >
              {t('sessionDetails.history')}
            </button>
            <button
              onClick={() => setActiveTab('boxes')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'boxes'
                  ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300'
                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
              }`}
            >
              {t('sessionDetails.boxes')}
            </button>
            <button
              onClick={() => setActiveTab('overrides')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'overrides'
                  ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300'
                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
              }`}
            >
              {t('sessionDetails.overrides') || 'Overrides'}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'assignment' ? (
            <SessionAssignmentTab
              session={session}
              subSessions={subSessions}
              canEdit={canEdit}
              canSplit={canSplit}
              isReadOnly={isReadOnly}
              isSplitting={isSplitting}
              stationSplitEntries={stationSplitEntries}
              availableStations={availableStations}
              sortedOperators={sortedOperators}
              availableOperators={availableOperators}
              availablePackers={availablePackers}
              efficiencyMap={efficiencyMap}
              efficiencySortBy={efficiencySortBy}
              setEfficiencySortBy={setEfficiencySortBy}
              totalAllocatedWeight={totalAllocatedWeight}
              isSplitValid={isSplitValid}
              entriesBelowMinWeight={entriesBelowMinWeight}
              canEditSubSession={canEditSubSession}
              updateStationEntry={updateStationEntry}
              openSubdivideModal={openSubdivideModal}
              setShowAddMaterialModal={setShowAddMaterialModal}
              setPendingStationChange={setPendingStationChange}
              setShowStationChangeModal={setShowStationChangeModal}
              onSubSessionStatusChange={handleStatusChange}
            />
          ) : activeTab === 'details' ? (
            <SessionDetailsTab
              session={session}
              subSessions={subSessions}
              locationCodes={locationCodes}
              canEdit={canEdit}
              setSourceLocationMutation={setSourceLocationMutation}
              onClose={onClose}
            />
          ) : activeTab === 'history' ? (
            <SessionHistoryTab
              session={session}
              isLoadingHistory={isLoadingHistory}
              historyData={historyData}
            />
          ) : activeTab === 'boxes' ? (
            <SessionBoxesTab
              session={session}
              isLoadingHistory={isLoadingHistory}
              historyData={historyData}
              showAllCancelled={showAllCancelled}
              setShowAllCancelled={setShowAllCancelled}
              expandedDiscardedLabels={expandedDiscardedLabels}
              setExpandedDiscardedLabels={setExpandedDiscardedLabels}
              setReprintBoxId={setReprintBoxId}
            />
          ) : activeTab === 'overrides' ? (
            /* Overrides Tab */
            <div className="h-full">
              <OverrideHistoryTable
                sessionId={session.session_number}
                showSessionColumn={false}
                showFilters={false}
                compact={true}
              />
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="p-6 border-t dark:border-gray-700">
          <div className="flex justify-between items-center">
            {/* Left side - Session actions */}
            <div className="flex gap-2">
              {canCancelSession && (
                <button
                  onClick={() => setShowCancelModal(true)}
                  disabled={isSessionControlLoading}
                  className="px-4 py-2 bg-gray-100 text-red-600/70 border border-gray-300 rounded-lg hover:bg-red-50 hover:border-red-200 dark:bg-gray-800 dark:text-red-400/70 dark:border-gray-600 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSessionControlLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <XCircle className="w-4 h-4" />
                  )}
                  {t('sessionDetails.cancelSession')}
                </button>
              )}
            </div>
            {/* Right side - Form actions */}
            <div className="flex gap-2">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                {t('common.cancel')}
              </button>
              {/* Unified Save button - handles both updating existing and creating new sub-sessions */}
              {/* Show for active/paused sessions too (not just planned/ready) so we can modify weights/assignments */}
              {activeTab === 'assignment' && !isReadOnly && (
                <button
                  onClick={handleUnifiedSave}
                  disabled={!isSplitValid || !hasChanges || isSavingAssignments || splitSessionMutation.isLoading || isReadOnly}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {(isSavingAssignments || splitSessionMutation.isLoading) ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                  {t('common.save')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <SessionModals
        session={session}
        subSessions={subSessions}
        availableStations={availableStations}
        stationSplitEntries={stationSplitEntries}
        isSessionControlLoading={isSessionControlLoading}
        historyData={historyData}
        showPauseModal={showPauseModal}
        setShowPauseModal={setShowPauseModal}
        pauseReason={pauseReason}
        setPauseReason={setPauseReason}
        handlePauseSession={handlePauseSession}
        showStationChangeModal={showStationChangeModal}
        setShowStationChangeModal={setShowStationChangeModal}
        pendingStationChange={pendingStationChange}
        setPendingStationChange={setPendingStationChange}
        stationChangeReason={stationChangeReason}
        setStationChangeReason={setStationChangeReason}
        stationChangeOtherReason={stationChangeOtherReason}
        setStationChangeOtherReason={setStationChangeOtherReason}
        handleConfirmStationChange={handleConfirmStationChange}
        isSavingAssignments={isSavingAssignments}
        updateStationEntry={updateStationEntry}
        showSubdivideModal={showSubdivideModal}
        setShowSubdivideModal={setShowSubdivideModal}
        subdivideSubSessionId={subdivideSubSessionId}
        setSubdivideSubSessionId={setSubdivideSubSessionId}
        subdivideWeight={subdivideWeight}
        setSubdivideWeight={setSubdivideWeight}
        subdivideStationId={subdivideStationId}
        setSubdivideStationId={setSubdivideStationId}
        subdivideReason={subdivideReason}
        setSubdivideReason={setSubdivideReason}
        subdivideReasonOther={subdivideReasonOther}
        setSubdivideReasonOther={setSubdivideReasonOther}
        isSubdividing={isSubdividing}
        handleConfirmSubdivide={handleConfirmSubdivide}
        getRemainingWeight={getRemainingWeight}
        showCompleteModal={showCompleteModal}
        setShowCompleteModal={setShowCompleteModal}
        completeSubSessionId={completeSubSessionId}
        setCompleteSubSessionId={setCompleteSubSessionId}
        completeReason={completeReason}
        setCompleteReason={setCompleteReason}
        isCompletingSession={isCompletingSession}
        handleCompleteSubSession={handleCompleteSubSession}
        showCancelSubSessionConfirm={showCancelSubSessionConfirm}
        setShowCancelSubSessionConfirm={setShowCancelSubSessionConfirm}
        cancelSubSessionId={cancelSubSessionId}
        setCancelSubSessionId={setCancelSubSessionId}
        isCancellingSubSession={isCancellingSubSession}
        confirmCancelSubSession={confirmCancelSubSession}
        reprintBoxId={reprintBoxId}
        setReprintBoxId={setReprintBoxId}
        reprintReason={reprintReason}
        setReprintReason={setReprintReason}
        reprintReasons={reprintReasons}
        isReprinting={isReprinting}
        handleReprint={handleReprint}
        showLabelPreview={showLabelPreview}
        setShowLabelPreview={setShowLabelPreview}
        labelPreviewHtml={labelPreviewHtml}
        numberOfCopies={numberOfCopies}
        showAddMaterialModal={showAddMaterialModal}
        setShowAddMaterialModal={setShowAddMaterialModal}
        handleAddMaterial={handleAddMaterial}
        pendingMaterials={pendingMaterials}
        stationConflictInfo={stationConflictInfo}
        handleCancelQueuePriority={handleCancelQueuePriority}
        handleConfirmQueuePriority={handleConfirmQueuePriority}
        showUnsavedChangesModal={showUnsavedChangesModal}
        setShowUnsavedChangesModal={setShowUnsavedChangesModal}
        handleConfirmDiscard={handleConfirmDiscard}
      />

      {/* Shared cancel flow: reason dropdown → password confirmation (WCO-2706) */}
      <CancelSessionModal
        session={showCancelModal ? session : null}
        onClose={() => setShowCancelModal(false)}
        onCancelled={onClose}
      />
    </div>
  );
};

export default SessionDetailsModal;
